// Project: GalaxyComplete
// Created: 9/28/20 by sammy
// File: MigrationCommon

import * as React from "react";
import {
    GalaxyMigrateDeploymentDetails,
    GalaxyMigrateManagedVolumeInfo,
    GalaxyMigrateMigrationSessionInfo,
    GalaxyMigrateStorageConfig,
} from "gc-web-proto/galaxycompletepb/apipb/domainpb/galaxymigrate_pb";
import { Alert, AlertTitle, Box, Button, linearProgressClasses, Stack, SvgIcon, SvgIconProps, Tooltip, Typography, useTheme } from "@mui/material";
import { getProjectSubRouteTemplate, CMC_MIGRATION_SUBROUTE, PROJECT_SUBROUTE } from "../app/AppRoutes";
import { generateMigrationSessionSubPathRoute } from "./migrationSessionPaths";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { SyncQos } from "gc-web-proto/galaxymigratepb/galaxy_migrate_types_pb";
import { useAppServices } from "../app/services";
import { useIsTableEmpty } from "../../common/table/TableCommon";
import { IoSync } from "react-icons/io5";
import { HiScissors } from "react-icons/hi";
import { GalaxyMigrateMigrationService, GmMigrationType, GmMigrationVolumeType } from "./GmMigrationService";
import { IoMdPause } from "react-icons/io";
import { MdDone, MdOutlineCancel, MdOutlineRestartAlt } from "react-icons/md";
import { isDeploymentConnected, isMinMtdiVersion } from "../deployment/DeploymentCommon";
import { DeploymentIdentityInfo, DeploymentInfo } from "gc-web-proto/galaxycompletepb/apipb/domainpb/deployment_pb";
import { RpcError, StatusCode } from "grpc-web";
import { formatKnownDataType, KnownDataType } from "../../common/utils/formatter";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { MigrationQosScheduleType, QosDailyScheduleIntervalType } from "./wizard/GmMigrationSessionParametersForm";
import { MigrationSession } from "gc-web-proto/galaxymigratepb/galaxy_migrate_types_pb";
import { createDateObjectFromMinutes, getMinutesFromTime } from "../../common/utils/dateUtil";
import { FormSelectableCardConfig } from "../../common/form/FormComponents";
import MinimumMode from "../../assets/migrationSession/minimum.png";
import ModerateMode from "../../assets/migrationSession/moderate.png";
import AggressiveMode from "../../assets/migrationSession/aggressive.png";
import RelentlessMode from "../../assets/migrationSession/relentless.png";
import { DialogService } from "../core/dialog/GlobalDialogState";
import { KnownArticle } from "../help/HelpCommon";
import { useOpenHelpArticle } from "../help/hooks/help_hooks";
import { ColumnSortFilterConfig, ListFilterableField } from "../core/data/ListData";
import { ListMigrationSessions } from "gc-web-proto/galaxycompletepb/apipb/gmapipb/galaxymigrate_api_pb";
import { SortState } from "../../common/table/DataTable";
import { ListGalaxyMigrateDeployments } from "gc-web-proto/galaxycompletepb/apipb/deployment_api_pb";
import { FilterParams } from "gc-web-proto/galaxycompletepb/commonpb/datafilter_pb";
import { DeploymentHostEnvironment, DeploymentHostEnvironmentFilter } from "gc-web-proto/galaxycompletepb/apipb/domainpb/enumpb/deployment_host_environment_pb";
import { getHostEnvDisplayName } from "../galaxymigrate/GalaxyMigrateCommon";
import { MtdiOsType, MtdiOsTypeFilter } from "gc-web-proto/galaxycompletepb/apipb/domainpb/enumpb/mtdi_os_type_pb";
import { getMtdiDeploymentOsDisplayName } from "../project/ProjectCommon";
import { GmMigrationSessionStatus, GmMigrationSessionStatusFilter } from "gc-web-proto/galaxycompletepb/apipb/domainpb/enumpb/gm_migration_session_status_pb";
import { Trans, useTranslation } from "react-i18next";
import { appI18n } from "../i18n/i18n";

export const CutoverIcon: React.FC<Partial<SvgIconProps>> = (p) => {
    return (
        <SvgIcon {...p}>
            <HiScissors />
        </SvgIcon>
    );
};

export const CancelIcon: React.FC<Partial<SvgIconProps>> = (p) => {
    return (
        <SvgIcon {...p}>
            <MdOutlineCancel />
        </SvgIcon>
    );
};

export const CompleteIcon: React.FC<Partial<SvgIconProps>> = (p) => {
    return (
        <SvgIcon {...p}>
            <MdDone />
        </SvgIcon>
    );
};

export const RestartIcon: React.FC<Partial<SvgIconProps>> = (p) => {
    return (
        <SvgIcon {...p}>
            <MdOutlineRestartAlt />
        </SvgIcon>
    );
};

export const SynchronizeIcon: React.FC<Partial<SvgIconProps>> = (p) => {
    return (
        <SvgIcon {...p}>
            <IoSync />
        </SvgIcon>
    );
};
export const SuspendSynchronizeIcon: React.FC<Partial<SvgIconProps>> = (p) => {
    return (
        <SvgIcon {...p}>
            <IoMdPause />
        </SvgIcon>
    );
};

export const getListMigrationSessionsSortFilterConfig = (): ColumnSortFilterConfig<
    ListMigrationSessions.Request,
    ListMigrationSessions.Request.Sort,
    any,
    any
> => {
    return {
        sort: {
            defaultSort: {
                fieldId: ListMigrationSessions.Request.Sort.Field.CREATED_AT,
                label: getListMigrationSessionsSortFieldLabel(ListMigrationSessions.Request.Sort.Field.CREATED_AT),
            },
            defaultSortDesc: true,
            sortFields: [
                {
                    fieldId: ListMigrationSessions.Request.Sort.Field.HOST_NAME,
                    label: getListMigrationSessionsSortFieldLabel(ListMigrationSessions.Request.Sort.Field.HOST_NAME),
                },
                {
                    fieldId: ListMigrationSessions.Request.Sort.Field.CREATED_AT,
                    label: getListMigrationSessionsSortFieldLabel(ListMigrationSessions.Request.Sort.Field.CREATED_AT),
                },
                {
                    fieldId: ListMigrationSessions.Request.Sort.Field.MIGRATION_PROGRESS,
                    label: getListMigrationSessionsSortFieldLabel(ListMigrationSessions.Request.Sort.Field.MIGRATION_PROGRESS),
                },
                {
                    fieldId: ListMigrationSessions.Request.Sort.Field.MIGRATION_STATUS,
                    label: getListMigrationSessionsSortFieldLabel(ListMigrationSessions.Request.Sort.Field.MIGRATION_STATUS),
                },
                {
                    fieldId: ListMigrationSessions.Request.Sort.Field.HOST_OS,
                    label: getListMigrationSessionsSortFieldLabel(ListMigrationSessions.Request.Sort.Field.HOST_OS),
                },
                {
                    fieldId: ListMigrationSessions.Request.Sort.Field.TOTAL_CAPACITY,
                    label: getListMigrationSessionsSortFieldLabel(ListMigrationSessions.Request.Sort.Field.TOTAL_CAPACITY),
                },
                {
                    fieldId: ListMigrationSessions.Request.Sort.Field.TOTAL_VOLUMES,
                    label: getListMigrationSessionsSortFieldLabel(ListMigrationSessions.Request.Sort.Field.TOTAL_VOLUMES),
                },
                {
                    fieldId: ListMigrationSessions.Request.Sort.Field.THIN_PERCENTAGE,
                    label: getListMigrationSessionsSortFieldLabel(ListMigrationSessions.Request.Sort.Field.THIN_PERCENTAGE),
                },
                {
                    fieldId: ListMigrationSessions.Request.Sort.Field.AVERAGE_TRANSFER_THROUGHPUT,
                    label: getListMigrationSessionsSortFieldLabel(ListMigrationSessions.Request.Sort.Field.AVERAGE_TRANSFER_THROUGHPUT),
                },
                {
                    fieldId: ListMigrationSessions.Request.Sort.Field.AVERAGE_THROUGHPUT,
                    label: getListMigrationSessionsSortFieldLabel(ListMigrationSessions.Request.Sort.Field.AVERAGE_THROUGHPUT),
                },
                {
                    fieldId: ListMigrationSessions.Request.Sort.Field.CURRENT_THROUGHPUT,
                    label: getListMigrationSessionsSortFieldLabel(ListMigrationSessions.Request.Sort.Field.CURRENT_THROUGHPUT),
                },
            ],
            getSortParam: (s: SortState) => new ListMigrationSessions.Request.Sort().setField(s.field.fieldId).setDesc(s.descending.value),
        },
        filter: [
            {
                fieldId: ListMigrationSessions.Request.Filter.FieldCase.HOST_NAME,
                label: getListMigrationSessionsFilterFieldLabel(ListMigrationSessions.Request.Filter.FieldCase.HOST_NAME),
                filterType: "string",
                addFilterToRequest: (r: ListMigrationSessions.Request, f: FilterParams.StringFilter) => {
                    r.addFilterParams(new ListMigrationSessions.Request.Filter().setHostName(f));
                },
            },
            {
                fieldId: ListMigrationSessions.Request.Filter.FieldCase.HOST_ENVIRONMENT,
                label: getListMigrationSessionsFilterFieldLabel(ListMigrationSessions.Request.Filter.FieldCase.HOST_ENVIRONMENT),
                filterType: "enum",
                enumLabel: appI18n.t("cmcMigration:sessionList.filter.hostEnvironment"),
                enumValuesList: Object.values(DeploymentHostEnvironment.DeploymentHostEnvironment),
                getFormValueDisplayLabel: (v: DeploymentHostEnvironment.DeploymentHostEnvironment) => getHostEnvDisplayName(v),
                createParamFromFormValue: (v: DeploymentHostEnvironment.DeploymentHostEnvironment) =>
                    new DeploymentHostEnvironmentFilter().setHas(new DeploymentHostEnvironment().setValue(v)),
                addFilterToRequest: (r: ListMigrationSessions.Request, f: DeploymentHostEnvironmentFilter) => {
                    r.addFilterParams(new ListMigrationSessions.Request.Filter().setHostEnvironment(f));
                },
            } as ListFilterableField<ListMigrationSessions.Request, DeploymentHostEnvironmentFilter, DeploymentHostEnvironment.DeploymentHostEnvironment>,
            {
                fieldId: ListMigrationSessions.Request.Filter.FieldCase.OS_TYPE,
                label: getListMigrationSessionsFilterFieldLabel(ListMigrationSessions.Request.Filter.FieldCase.OS_TYPE),
                filterType: "enum",
                enumLabel: appI18n.t("cmcMigration:sessionList.filter.osType"),
                enumValuesList: Object.values(MtdiOsType.MtdiOsType),
                getFormValueDisplayLabel: (v: MtdiOsType.MtdiOsType) => getMtdiDeploymentOsDisplayName(v),
                createParamFromFormValue: (v: MtdiOsType.MtdiOsType) => new MtdiOsTypeFilter().setHas(new MtdiOsType().setValue(v)),
                addFilterToRequest: (r: ListMigrationSessions.Request, f: MtdiOsTypeFilter) => {
                    r.addFilterParams(new ListMigrationSessions.Request.Filter().setOsType(f));
                },
            } as ListFilterableField<ListMigrationSessions.Request, MtdiOsTypeFilter, MtdiOsType.MtdiOsType>,
            {
                fieldId: ListMigrationSessions.Request.Filter.FieldCase.SESSION_ID,
                label: getListMigrationSessionsFilterFieldLabel(ListMigrationSessions.Request.Filter.FieldCase.SESSION_ID),
                filterType: "string",
                addFilterToRequest: (r: ListMigrationSessions.Request, f: FilterParams.StringFilter) => {
                    r.addFilterParams(new ListMigrationSessions.Request.Filter().setSessionId(f));
                },
            },
            {
                fieldId: ListMigrationSessions.Request.Filter.FieldCase.DESCRIPTION,
                label: getListMigrationSessionsFilterFieldLabel(ListMigrationSessions.Request.Filter.FieldCase.DESCRIPTION),
                filterType: "string",
                addFilterToRequest: (r: ListMigrationSessions.Request, f: FilterParams.StringFilter) => {
                    r.addFilterParams(new ListMigrationSessions.Request.Filter().setDescription(f));
                },
            },
            {
                fieldId: ListMigrationSessions.Request.Filter.FieldCase.MIGRATION_PROGRESS,
                label: getListMigrationSessionsFilterFieldLabel(ListMigrationSessions.Request.Filter.FieldCase.MIGRATION_PROGRESS),
                filterType: "number",
                numType: "percentage",
                addFilterToRequest: (r: ListMigrationSessions.Request, f: FilterParams.NumberFilter) => {
                    r.addFilterParams(new ListMigrationSessions.Request.Filter().setMigrationProgress(f));
                },
            },
            {
                fieldId: ListMigrationSessions.Request.Filter.FieldCase.MIGRATION_STATUS,
                label: getListMigrationSessionsFilterFieldLabel(ListMigrationSessions.Request.Filter.FieldCase.MIGRATION_STATUS),
                filterType: "enum",
                enumLabel: appI18n.t("cmcMigration:sessionList.filter.sessionStatus"),
                enumValuesList: Object.values(GmMigrationSessionStatus.GmMigrationSessionStatus),
                addFilterToRequest: (r: ListMigrationSessions.Request, f: GmMigrationSessionStatusFilter) => {
                    r.addFilterParams(new ListMigrationSessions.Request.Filter().setMigrationStatus(f));
                },
                getFormValueDisplayLabel: (v: GmMigrationSessionStatus.GmMigrationSessionStatus) => getGmMigrationSessionStatusLabel(v),
                createParamFromFormValue: (v: GmMigrationSessionStatus.GmMigrationSessionStatus) =>
                    new GmMigrationSessionStatusFilter().setHas(new GmMigrationSessionStatus().setValue(v)),
            },
            {
                fieldId: ListMigrationSessions.Request.Filter.FieldCase.CREATED_AT,
                label: getListMigrationSessionsFilterFieldLabel(ListMigrationSessions.Request.Filter.FieldCase.CREATED_AT),
                filterType: "date",
                addFilterToRequest: (r: ListMigrationSessions.Request, f: FilterParams.DateFilter) => {
                    r.addFilterParams(new ListMigrationSessions.Request.Filter().setCreatedAt(f));
                },
            },
            {
                fieldId: ListMigrationSessions.Request.Filter.FieldCase.THIN_PERCENTAGE,
                label: getListMigrationSessionsFilterFieldLabel(ListMigrationSessions.Request.Filter.FieldCase.THIN_PERCENTAGE),
                filterType: "number",
                numType: "percentage",
                addFilterToRequest: (r: ListMigrationSessions.Request, f: FilterParams.NumberFilter) => {
                    r.addFilterParams(new ListMigrationSessions.Request.Filter().setThinPercentage(f));
                },
            },
            {
                fieldId: ListMigrationSessions.Request.Filter.FieldCase.AVERAGE_THROUGHPUT,
                label: getListMigrationSessionsFilterFieldLabel(ListMigrationSessions.Request.Filter.FieldCase.AVERAGE_THROUGHPUT),
                filterType: "number",
                numType: "throughput",
                addFilterToRequest: (r: ListMigrationSessions.Request, f: FilterParams.NumberFilter) => {
                    r.addFilterParams(new ListMigrationSessions.Request.Filter().setAverageThroughput(f));
                },
            },
            {
                fieldId: ListMigrationSessions.Request.Filter.FieldCase.AVERAGE_TRANSFER_THROUGHPUT,
                label: getListMigrationSessionsFilterFieldLabel(ListMigrationSessions.Request.Filter.FieldCase.AVERAGE_TRANSFER_THROUGHPUT),
                filterType: "number",
                numType: "throughput",
                addFilterToRequest: (r: ListMigrationSessions.Request, f: FilterParams.NumberFilter) => {
                    r.addFilterParams(new ListMigrationSessions.Request.Filter().setAverageTransferThroughput(f));
                },
            },
            {
                fieldId: ListMigrationSessions.Request.Filter.FieldCase.CREATED_BY,
                label: getListMigrationSessionsFilterFieldLabel(ListMigrationSessions.Request.Filter.FieldCase.CREATED_BY),
                filterType: "simpleString",
                addFilterToRequest: (r: ListMigrationSessions.Request, f: FilterParams.SimpleStringFilter) => {
                    r.addFilterParams(new ListMigrationSessions.Request.Filter().setCreatedBy(f));
                },
                helperText: appI18n.t("cmcMigration:sessionList.filter.createdByHelp"),
            },
            {
                fieldId: ListMigrationSessions.Request.Filter.FieldCase.TOTAL_VOLUMES,
                label: getListMigrationSessionsFilterFieldLabel(ListMigrationSessions.Request.Filter.FieldCase.TOTAL_VOLUMES),
                filterType: "number",
                addFilterToRequest: (r: ListMigrationSessions.Request, f: FilterParams.NumberFilter) => {
                    r.addFilterParams(new ListMigrationSessions.Request.Filter().setTotalVolumes(f));
                },
            },
            {
                fieldId: ListMigrationSessions.Request.Filter.FieldCase.TOTAL_CAPACITY,
                label: getListMigrationSessionsFilterFieldLabel(ListMigrationSessions.Request.Filter.FieldCase.TOTAL_CAPACITY),
                filterType: "number",
                numType: "capacity",
                addFilterToRequest: (r: ListMigrationSessions.Request, f: FilterParams.NumberFilter) => {
                    r.addFilterParams(new ListMigrationSessions.Request.Filter().setTotalCapacity(f));
                },
            },
            {
                fieldId: ListMigrationSessions.Request.Filter.FieldCase.MOCK_MIGRATION,
                label: getListMigrationSessionsFilterFieldLabel(ListMigrationSessions.Request.Filter.FieldCase.MOCK_MIGRATION),
                filterType: "bool",
                addFilterToRequest: (r: ListMigrationSessions.Request, f: FilterParams.BoolFilter) => {
                    r.addFilterParams(new ListMigrationSessions.Request.Filter().setMockMigration(f));
                },
            },
            {
                fieldId: ListMigrationSessions.Request.Filter.FieldCase.LABEL,
                label: getListMigrationSessionsFilterFieldLabel(ListMigrationSessions.Request.Filter.FieldCase.LABEL),
                filterType: "simpleString",
                addFilterToRequest: (r: ListMigrationSessions.Request, f: FilterParams.SimpleStringFilter) => {
                    r.addFilterParams(new ListMigrationSessions.Request.Filter().setLabel(f));
                },
            },
        ],
    };
};

const getListMigrationSessionsSortFieldLabel = (field: ListMigrationSessions.Request.Sort.Field): string => {
    if (field === ListMigrationSessions.Request.Sort.Field.CREATED_AT) {
        return appI18n.t("cmcMigration:sessionList.sort.createdAt");
    }
    if (field === ListMigrationSessions.Request.Sort.Field.HOST_NAME) {
        return appI18n.t("cmcMigration:sessionList.sort.hostName");
    }
    if (field === ListMigrationSessions.Request.Sort.Field.MIGRATION_PROGRESS) {
        return appI18n.t("cmcMigration:sessionList.sort.migrationProgress");
    }
    if (field === ListMigrationSessions.Request.Sort.Field.MIGRATION_STATUS) {
        return appI18n.t("cmcMigration:sessionList.sort.migrationStatus");
    }
    if (field === ListMigrationSessions.Request.Sort.Field.HOST_OS) {
        return appI18n.t("cmcMigration:sessionList.sort.hostOs");
    }
    if (field === ListMigrationSessions.Request.Sort.Field.AVERAGE_THROUGHPUT) {
        return appI18n.t("cmcMigration:sessionList.sort.averageThroughput");
    }
    if (field === ListMigrationSessions.Request.Sort.Field.AVERAGE_TRANSFER_THROUGHPUT) {
        return appI18n.t("cmcMigration:sessionList.sort.averageTransferThroughput");
    }
    if (field === ListMigrationSessions.Request.Sort.Field.CURRENT_THROUGHPUT) {
        return appI18n.t("cmcMigration:sessionList.sort.currentThroughput");
    }
    if (field === ListMigrationSessions.Request.Sort.Field.THIN_PERCENTAGE) {
        return appI18n.t("cmcMigration:sessionList.sort.thinPercentage");
    }
    if (field === ListMigrationSessions.Request.Sort.Field.TOTAL_CAPACITY) {
        return appI18n.t("cmcMigration:sessionList.sort.totalCapacity");
    }
    if (field === ListMigrationSessions.Request.Sort.Field.TOTAL_VOLUMES) {
        return appI18n.t("cmcMigration:sessionList.sort.totalVolumes");
    }
    return appI18n.t("common:status.unknown");
};

const getListMigrationSessionsFilterFieldLabel = (field: ListMigrationSessions.Request.Filter.FieldCase): string => {
    if (field === ListMigrationSessions.Request.Filter.FieldCase.CREATED_AT) {
        return appI18n.t("cmcMigration:sessionList.filter.createdAt");
    }
    if (field === ListMigrationSessions.Request.Filter.FieldCase.HOST_NAME) {
        return appI18n.t("cmcMigration:sessionList.filter.hostName");
    }
    if (field === ListMigrationSessions.Request.Filter.FieldCase.MIGRATION_PROGRESS) {
        return appI18n.t("cmcMigration:sessionList.filter.migrationProgress");
    }
    if (field === ListMigrationSessions.Request.Filter.FieldCase.MIGRATION_STATUS) {
        return appI18n.t("cmcMigration:sessionList.filter.migrationStatus");
    }
    if (field === ListMigrationSessions.Request.Filter.FieldCase.HOST_ENVIRONMENT) {
        return appI18n.t("cmcMigration:sessionList.filter.hostOs");
    }
    if (field === ListMigrationSessions.Request.Filter.FieldCase.AVERAGE_THROUGHPUT) {
        return appI18n.t("cmcMigration:sessionList.filter.averageThroughput");
    }
    if (field === ListMigrationSessions.Request.Filter.FieldCase.AVERAGE_TRANSFER_THROUGHPUT) {
        return appI18n.t("cmcMigration:sessionList.filter.averageTransferThroughput");
    }
    if (field === ListMigrationSessions.Request.Filter.FieldCase.OS_TYPE) {
        return appI18n.t("cmcMigration:sessionList.filter.osType");
    }
    if (field === ListMigrationSessions.Request.Filter.FieldCase.THIN_PERCENTAGE) {
        return appI18n.t("cmcMigration:sessionList.filter.thinPercentage");
    }
    if (field === ListMigrationSessions.Request.Filter.FieldCase.TOTAL_CAPACITY) {
        return appI18n.t("cmcMigration:sessionList.filter.totalCapacity");
    }
    if (field === ListMigrationSessions.Request.Filter.FieldCase.TOTAL_VOLUMES) {
        return appI18n.t("cmcMigration:sessionList.filter.totalVolumes");
    }
    if (field === ListMigrationSessions.Request.Filter.FieldCase.CREATED_BY) {
        return appI18n.t("cmcMigration:sessionList.filter.createdBy");
    }
    if (field === ListMigrationSessions.Request.Filter.FieldCase.DESCRIPTION) {
        return appI18n.t("cmcMigration:sessionList.filter.description");
    }
    if (field === ListMigrationSessions.Request.Filter.FieldCase.SESSION_ID) {
        return appI18n.t("cmcMigration:sessionList.filter.sessionId");
    }
    if (field === ListMigrationSessions.Request.Filter.FieldCase.LABEL) {
        return appI18n.t("cmcMigration:sessionList.filter.label");
    }
    if (field === ListMigrationSessions.Request.Filter.FieldCase.MOCK_MIGRATION) {
        return appI18n.t("cmcMigration:sessionList.filter.mockMigration");
    }
    return appI18n.t("common:status.unknown");
};

export const getGmMigrationSessionStatusLabel = (s: GmMigrationSessionStatus.GmMigrationSessionStatus) => {
    return {
        [GmMigrationSessionStatus.GmMigrationSessionStatus.FAILED.valueOf()]: appI18n.t("cmcMigration:status.session.failed"),
        [GmMigrationSessionStatus.GmMigrationSessionStatus.CANCELLED.valueOf()]: appI18n.t("cmcMigration:status.session.canceled"),
        [GmMigrationSessionStatus.GmMigrationSessionStatus.HOLDING_IO.valueOf()]: appI18n.t("cmcMigration:status.session.holdingIo"),
        [GmMigrationSessionStatus.GmMigrationSessionStatus.IN_ERROR.valueOf()]: appI18n.t("cmcMigration:status.session.inError"),
        [GmMigrationSessionStatus.GmMigrationSessionStatus.MIRRORING.valueOf()]: appI18n.t("cmcMigration:status.session.mirroring"),
        [GmMigrationSessionStatus.GmMigrationSessionStatus.TRACKING.valueOf()]: appI18n.t("cmcMigration:status.session.trackingChanges"),
        [GmMigrationSessionStatus.GmMigrationSessionStatus.SYNCING.valueOf()]: appI18n.t("cmcMigration:status.session.synchronizing"),
        [GmMigrationSessionStatus.GmMigrationSessionStatus.SUSPENDED.valueOf()]: appI18n.t("cmcMigration:status.session.suspended"),
        [GmMigrationSessionStatus.GmMigrationSessionStatus.STANDING_IN.valueOf()]: appI18n.t("cmcMigration:status.session.standingIn"),
        [GmMigrationSessionStatus.GmMigrationSessionStatus.CUTOVER.valueOf()]: appI18n.t("cmcMigration:status.session.cutover"),
        [GmMigrationSessionStatus.GmMigrationSessionStatus.COMPLETED.valueOf()]: appI18n.t("cmcMigration:status.session.completed"),
        [GmMigrationSessionStatus.GmMigrationSessionStatus.NEW.valueOf()]: appI18n.t("cmcMigration:status.session.new"),
    }[s];
};

const gmMigrationSessionInfoStatusMap: Record<GalaxyMigrateMigrationSessionInfo.Status, GmMigrationSessionStatus.GmMigrationSessionStatus> = {
    [GalaxyMigrateMigrationSessionInfo.Status.UNKNOWN]: GmMigrationSessionStatus.GmMigrationSessionStatus.UNKNOWN,
    [GalaxyMigrateMigrationSessionInfo.Status.IN_ERROR]: GmMigrationSessionStatus.GmMigrationSessionStatus.IN_ERROR,
    [GalaxyMigrateMigrationSessionInfo.Status.FAILED]: GmMigrationSessionStatus.GmMigrationSessionStatus.FAILED,
    [GalaxyMigrateMigrationSessionInfo.Status.NEW]: GmMigrationSessionStatus.GmMigrationSessionStatus.NEW,
    [GalaxyMigrateMigrationSessionInfo.Status.TRACKING]: GmMigrationSessionStatus.GmMigrationSessionStatus.TRACKING,
    [GalaxyMigrateMigrationSessionInfo.Status.SYNCING]: GmMigrationSessionStatus.GmMigrationSessionStatus.SYNCING,
    [GalaxyMigrateMigrationSessionInfo.Status.HOLDING_IO]: GmMigrationSessionStatus.GmMigrationSessionStatus.HOLDING_IO,
    [GalaxyMigrateMigrationSessionInfo.Status.SUSPENDED]: GmMigrationSessionStatus.GmMigrationSessionStatus.SUSPENDED,
    [GalaxyMigrateMigrationSessionInfo.Status.MIRRORING]: GmMigrationSessionStatus.GmMigrationSessionStatus.MIRRORING,
    [GalaxyMigrateMigrationSessionInfo.Status.STANDING_IN]: GmMigrationSessionStatus.GmMigrationSessionStatus.STANDING_IN,
    [GalaxyMigrateMigrationSessionInfo.Status.CUTOVER]: GmMigrationSessionStatus.GmMigrationSessionStatus.CUTOVER,
    [GalaxyMigrateMigrationSessionInfo.Status.COMPLETED]: GmMigrationSessionStatus.GmMigrationSessionStatus.COMPLETED,
    [GalaxyMigrateMigrationSessionInfo.Status.CANCELLED]: GmMigrationSessionStatus.GmMigrationSessionStatus.CANCELLED,
};

export const renderGMSessionStatus = (s: GalaxyMigrateMigrationSessionInfo.Status): React.ReactNode => {
    let error = false;
    if ([GalaxyMigrateMigrationSessionInfo.Status.FAILED, GalaxyMigrateMigrationSessionInfo.Status.IN_ERROR].includes(s)) {
        error = true;
    }

    const text = getGmMigrationSessionStatusLabel(gmMigrationSessionInfoStatusMap[s]) || "---";
    return (
        <Typography color={error ? "error" : s === GalaxyMigrateMigrationSessionInfo.Status.COMPLETED.valueOf() ? "primary" : undefined} variant={"inherit"}>
            {text}
        </Typography>
    );
};

// Same shape as renderGMSessionStatus, but accepts the GmMigrationSessionStatus enum used by
// non-session contexts (e.g. DR ReplicationState.migrationSessionStatus).
export const renderGmMigrationSessionStatus = (s: GmMigrationSessionStatus.GmMigrationSessionStatus): React.ReactNode => {
    const error = s === GmMigrationSessionStatus.GmMigrationSessionStatus.FAILED || s === GmMigrationSessionStatus.GmMigrationSessionStatus.IN_ERROR;
    const isCompleted = s === GmMigrationSessionStatus.GmMigrationSessionStatus.COMPLETED;
    const text = getGmMigrationSessionStatusLabel(s) || "---";
    return (
        <Typography color={error ? "error" : isCompleted ? "primary" : undefined} variant={"inherit"}>
            {text}
        </Typography>
    );
};

export const getGmSessionIDSlug = (id: string) => id.slice(-4);

export { generateMigrationSessionDetailsPath, generateMigrationSessionSubPathRoute, generateMigrationSubPathRoute } from "./migrationSessionPaths";

export const useNavigateToProjectMigrationListScreen = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const p = generatePath(getProjectSubRouteTemplate(PROJECT_SUBROUTE.CMC_MIGRATIONS) + `/${CMC_MIGRATION_SUBROUTE.SESSIONS}`, { projectId });
    return () => navigate(p);
};

export const useNavigateToMigrationWizardScreen = (replace = false) => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const p = generatePath(getProjectSubRouteTemplate(PROJECT_SUBROUTE.CMC_MIGRATIONS) + `/${CMC_MIGRATION_SUBROUTE.WIZARD}`, { projectId });
    return () => navigate(p, { replace });
};

export const useNavigateToSessionEditScreen = () => {
    const navigate = useNavigate();
    const { projectId, sessionId } = useParams();
    const p = generateMigrationSessionSubPathRoute(projectId, sessionId, CMC_MIGRATION_SUBROUTE.EDIT);
    return () => navigate(p);
};

export const useNavigateToMigrationSessionDetails = () => {
    const navigate = useNavigate();
    const { projectId, sessionId } = useParams();
    return (id?: string) => {
        const p = generatePath(getProjectSubRouteTemplate(PROJECT_SUBROUTE.CMC_MIGRATIONS) + `/${CMC_MIGRATION_SUBROUTE.SESSIONS}/${sessionId || id}/`, {
            projectId,
        });
        navigate(p);
    };
};

export const useNavigateToQosScreen = (sourceVolUUID: string) => {
    const navigate = useNavigate();
    const { projectId, sessionId } = useParams();
    const p = generatePath(
        generateMigrationSessionSubPathRoute(projectId, sessionId, CMC_MIGRATION_SUBROUTE.VOLUMES) + "/:sessionVolumeUUID/" + CMC_MIGRATION_SUBROUTE.QOS,
        { sessionVolumeUUID: sourceVolUUID }
    );
    return () => navigate(p);
};

export const generateMigrationSessionWorkflowRunRoute = (projectId: string, sessionId: string, workflowId: number) => {
    return generatePath(generateMigrationSessionSubPathRoute(projectId, sessionId, CMC_MIGRATION_SUBROUTE.WORKFLOW_RUNS) + `/${workflowId}`);
};

export const getSyncQosImpactLevelLabel = (impactLevel: string | SyncQos.ImpactLevel) => {
    if (impactLevel === SyncQos.ImpactLevel.RELENTLESS) {
        return appI18n.t("cmcMigration:qos.impact.relentless");
    } else if (impactLevel === SyncQos.ImpactLevel.MINIMUM) {
        return appI18n.t("cmcMigration:qos.impact.minimum");
    } else if (impactLevel === SyncQos.ImpactLevel.MODERATE) {
        return appI18n.t("cmcMigration:qos.impact.moderate");
    } else if (impactLevel === SyncQos.ImpactLevel.AGGRESSIVE) {
        return appI18n.t("cmcMigration:qos.impact.aggressive");
    } else {
        return appI18n.t("cmcMigration:qos.impact.none");
    }
};

export const useHasMigrationSessions = () => {
    const { gmMigrationService } = useAppServices();
    return useIsTableEmpty(gmMigrationService.sessions);
};

export const renderGmMigrationTypeLabel = (type: GmMigrationType) => {
    if (type === GmMigrationType.LOCAL) {
        return appI18n.t("cmcMigration:migrationType.local");
    } else if (type === GmMigrationType.REMOTE) {
        return appI18n.t("cmcMigration:migrationType.remote");
    }
    return null;
};

export const renderGmMigrationVolumeTypeLabel = (type: GmMigrationVolumeType) => {
    if (type === GmMigrationVolumeType.DATA) {
        return appI18n.t("cmcMigration:migrationVolumeType.data");
    }
};

const DESTINATION_UNAVAILABLE_DELETE_ERROR_MESSAGE = "migration destination is unavailable";

const isDestinationUnavailableDeleteRpcFailure = (error: unknown) => {
    const rpcError = error as Partial<RpcError>;
    return (
        rpcError?.code === StatusCode.ABORTED &&
        typeof rpcError.message === "string" &&
        rpcError.message.toLowerCase().includes(DESTINATION_UNAVAILABLE_DELETE_ERROR_MESSAGE)
    );
};

export const useDeleteMigrationSession = (id?: string) => {
    const { gmMigrationService, dialogService, deploymentService } = useAppServices();
    const { t } = useTranslation(["cmcMigration", "common"]);
    const sessionId = id || gmMigrationService.currentSessionID;
    const sessionInfo = gmMigrationService.currentSession.data.getSessionInfo();
    const isBootSession = sessionInfo.getBootVolumeSession();
    const isCompleted = sessionInfo.getSessionStatus() === GalaxyMigrateMigrationSessionInfo.Status.COMPLETED;
    const destinationDeployment = sessionInfo.getDestinationDeployment();
    const destinationSystemId = destinationDeployment?.getSystemId();
    const destinationName = destinationDeployment?.getSystemName() || t("dialogs.delete.destinationFallback");
    const returnToMigrationList = useNavigateToProjectMigrationListScreen();

    const message =
        isBootSession || isCompleted ? (
            t("dialogs.delete.irreversible")
        ) : (
            <Typography>
                <Trans i18nKey={"dialogs.delete.warning"} ns={"cmcMigration"} components={{ break: <br /> }} />
            </Typography>
        );
    return async () => {
        const confirmed = await dialogService.addConfirmDialog({
            message,
            autoConfirmationQuestionLine: true,
            title: t("dialogs.delete.title"),
            typeToConfirm: "DELETE",
            cancelButtonProps: { variant: "outlined" },
            cancelButtonLabel: t("common:actions.cancel"),
            okButtonLabel: t("dialogs.delete.action"),
            okButtonProps: { variant: "contained", color: "error" },
        });
        if (!confirmed) {
            return false;
        }

        try {
            const deleted = await gmMigrationService.deleteSession(sessionId, false, false);
            if (deleted) {
                returnToMigrationList();
                return true;
            }
        } catch (deleteError) {
            if (!isDestinationUnavailableDeleteRpcFailure(deleteError)) {
                await dialogService.addErrorAlertDialog(deleteError as Error);
                return false;
            }

            if (destinationSystemId) {
                try {
                    const destinationDetails = await deploymentService.fetchDeploymentDetails(destinationSystemId);
                    if (isDeploymentConnected(destinationDetails)) {
                        await dialogService.addErrorAlertDialog(deleteError as Error);
                        return false;
                    }
                } catch {
                    // Dest host was already removed or cannot be loaded.
                    // Nothing left to confirm; trust the dest-unavailable error.
                }
            }

            const forceConfirmed = await dialogService.addConfirmDialog({
                title: t("dialogs.delete.force.title"),
                renderAdditionalContent: () => (
                    <Stack spacing={2}>
                        <Alert severity={"warning"}>
                            <AlertTitle>{t("dialogs.delete.force.offlineTitle", { destination: destinationName })}</AlertTitle>
                            {t("dialogs.delete.force.cleanupFailed")}
                        </Alert>
                        <Typography>{t("dialogs.delete.force.permanentWarning")}</Typography>
                        <Typography>{t("dialogs.delete.force.cleanupSkippedWarning")}</Typography>
                    </Stack>
                ),
                autoConfirmationQuestionLine: true,
                typeToConfirm: "GONE",
                cancelButtonProps: { variant: "outlined" },
                cancelButtonLabel: t("common:actions.cancel"),
                okButtonLabel: t("dialogs.delete.force.action"),
                okButtonProps: { variant: "contained", color: "error" },
            });
            if (!forceConfirmed) {
                return false;
            }

            const deleted = await gmMigrationService.deleteSession(sessionId, true);
            if (deleted) {
                returnToMigrationList();
                return true;
            }
        }
        return false;
    };
};

export const useSyncMigrationSession = (id?: string) => {
    const { gmMigrationService } = useAppServices();
    const sessionId = id || gmMigrationService.currentSessionID;
    return async () => {
        await gmMigrationService.syncSession(sessionId);
        await gmMigrationService.currentSession.fetchData();
        return true;
    };
};

export const useCutoverMigrationSession = (id?: string) => {
    const { gmMigrationService, dialogService } = useAppServices();
    const { t } = useTranslation("cmcMigration");
    const sessionId = id || gmMigrationService.currentSessionID;
    return async () => {
        const confirmed = await dialogService.addConfirmDialog({
            message: t("dialogs.cutover.message"),
        });
        if (confirmed) {
            await gmMigrationService.cutoverSession(sessionId);
        }
        await gmMigrationService.fetchSessionDetails(sessionId);
        return confirmed;
    };
};
export const useCompleteMigrationSession = (id?: string) => {
    const { gmMigrationService, dialogService } = useAppServices();
    const { t } = useTranslation("cmcMigration");
    const sessionId = id || gmMigrationService.currentSessionID;
    return async () => {
        const confirmed = await dialogService.addConfirmDialog({
            message: t("dialogs.complete.message"),
        });
        if (confirmed) {
            await gmMigrationService.completeSession(sessionId);
        }
        await gmMigrationService.fetchSessionDetails(sessionId);
        return confirmed;
    };
};

export const useFinalizeCutoverMigrationSession = (id?: string) => {
    const { gmMigrationService, dialogService } = useAppServices();
    const { t } = useTranslation("cmcMigration");
    const sessionId = id || gmMigrationService.currentSessionID;
    return async () => {
        const confirmed = await dialogService.addConfirmDialog({
            message: t("dialogs.finalizeCutover.message"),
        });
        if (confirmed) {
            await gmMigrationService.finalCutoverSession(sessionId);
            await gmMigrationService.currentSession.fetchData();
        }
        return confirmed;
    };
};

export const validateNotExceededMaxOutstandingIOForBootCutover = async (
    gmMigrationService: GalaxyMigrateMigrationService,
    dialogService: DialogService,
    sessionId: string,
    totalDataRemaining: number,
    reviewMigrationGuideButton: React.ReactNode
) => {
    const MAX_OUTSTANDING_DATA_BEFORE_CUTOVER = 1 * 1000 * 1000 * 1000;
    if (totalDataRemaining > MAX_OUTSTANDING_DATA_BEFORE_CUTOVER) {
        const warningHeader = appI18n.t("cmcMigration:dialogs.bootCutover.synchronizationRequiredTitle");
        const warningMsg = appI18n.t("cmcMigration:dialogs.bootCutover.synchronizationRequiredMessage", {
            remaining: formatKnownDataType(totalDataRemaining, KnownDataType.CAPACITY),
            maximum: formatKnownDataType(MAX_OUTSTANDING_DATA_BEFORE_CUTOVER, KnownDataType.CAPACITY),
        });
        await dialogService.addAlertDialog({
            title: warningHeader,
            message: warningMsg,
            renderAdditionalContent: (closeFn) => {
                const triggerSyncButton = (
                    <Button
                        variant={"contained"}
                        color={"secondary"}
                        size={"small"}
                        onClick={async () => {
                            await gmMigrationService.syncSession(sessionId);
                            await gmMigrationService.fetchSessionDetails(sessionId);
                            closeFn();
                        }}
                    >
                        {appI18n.t("cmcMigration:dialogs.bootCutover.synchronizeNow")}
                    </Button>
                );
                return (
                    <>
                        <p>{reviewMigrationGuideButton}</p>
                        <p>{triggerSyncButton}</p>
                    </>
                );
            },
        });
        return false;
    }
    return true;
};

export const useCutoverBootSession = (id?: string) => {
    const { gmMigrationService, dialogService } = useAppServices();
    const { t } = useTranslation("cmcMigration");
    const sessionId = id || gmMigrationService.currentSessionID;
    const totalDataRemaining = gmMigrationService.currentSession.data?.getSessionInfo().getSessionStats().getTotalRemaining();
    const openHelpArticle = useOpenHelpArticle();

    return async () => {
        const reviewMigrationGuideButton = (
            <Button variant={"outlined"} color={"neutral"} size={"small"} onClick={() => openHelpArticle(KnownArticle.MIGRATING_BOOT_VOLUMES)}>
                {t("dialogs.bootCutover.reviewGuide")}
            </Button>
        );

        const valid = await validateNotExceededMaxOutstandingIOForBootCutover(
            gmMigrationService,
            dialogService,
            sessionId,
            totalDataRemaining,
            reviewMigrationGuideButton
        );
        if (!valid) {
            return;
        }

        const header = t("dialogs.bootCutover.readyTitle");
        const message = t("dialogs.bootCutover.readyMessage");

        let confirmed = await dialogService.addConfirmDialog({
            title: header,
            message,
            renderAdditionalContent: () => {
                return (
                    <>
                        <p>{message}</p>
                        <p>{t("dialogs.bootCutover.shutdownInstruction")}</p>
                        <p>{t("dialogs.bootCutover.acknowledgement")}</p>
                        <p>{reviewMigrationGuideButton}</p>
                    </>
                );
            },
        });
        if (confirmed) {
            confirmed = await dialogService.addConfirmDialog({
                message: t("dialogs.bootCutover.finalConfirmation"),
            });
        }
        if (confirmed) {
            await gmMigrationService.cutoverSession(sessionId);
        }
        await gmMigrationService.fetchSessionDetails(sessionId);
        return confirmed;
    };
};

export const getChangeDataMapDisabledForSession = (
    status: GalaxyMigrateMigrationSessionInfo.Status,
    deployment: GalaxyMigrateDeploymentDetails,
    sourceVolCapacity: number
) => {
    const enabledStatuses = [
        GalaxyMigrateMigrationSessionInfo.Status.TRACKING,
        GalaxyMigrateMigrationSessionInfo.Status.IN_ERROR,
        GalaxyMigrateMigrationSessionInfo.Status.STANDING_IN,
        GalaxyMigrateMigrationSessionInfo.Status.SYNCING,
    ];
    if (!enabledStatuses.includes(status)) {
        return true;
    } else if (!isMinMtdiVersion(deployment, "8.5.0")) {
        return true;
    } else if (sourceVolCapacity > 8 * Math.pow(1024, 4)) {
        return true;
    } else {
        return false;
    }
};

export const getStorageConfigBlockDevice = (path: string, deviceList: Array<GalaxyMigrateStorageConfig.Device.AsObject>, isDeploymentWindows: boolean) => {
    if (!!deviceList) {
        for (const d of deviceList) {
            if (`${isDeploymentWindows ? "" : "/dev/"}${d.blockDevice.deviceName}` === path) {
                return d;
            }
            for (const link of d.blockDevice.deviceLinksList) {
                if (link === path) {
                    return d;
                }
            }
        }
    }

    return null;
};

export const getMigrationVolumeTooltip = (device: GalaxyMigrateStorageConfig.Device.AsObject, volume: GalaxyMigrateManagedVolumeInfo) => {
    if (!!device && !!volume) {
        return (
            <Box pt={1}>
                <Tooltip
                    title={
                        <Box>
                            <Box>
                                {appI18n.t("cmcMigration:volumeTooltip.deviceValue", {
                                    value: device.blockDevice.deviceName,
                                })}
                            </Box>
                            <Box>
                                {appI18n.t("cmcMigration:volumeTooltip.capacityValue", {
                                    value: formatKnownDataType(device.blockDevice.capacity, KnownDataType.CAPACITY),
                                })}
                            </Box>
                            <Box>
                                {appI18n.t("cmcMigration:volumeTooltip.typeValue", {
                                    value: device.blockDevice.deviceType,
                                })}
                            </Box>
                            {!!device.blockDevice.fsType && (
                                <Box>
                                    {appI18n.t("cmcMigration:volumeTooltip.fileSystemTypeValue", {
                                        value: device.blockDevice.fsType,
                                    })}
                                </Box>
                            )}
                            {!!device.blockDevice.label && (
                                <Box>
                                    {appI18n.t("cmcMigration:volumeTooltip.fileSystemLabelValue", {
                                        value: device.blockDevice.label,
                                    })}
                                </Box>
                            )}
                            {!!device.blockDevice.mountPoint && (
                                <Box>
                                    {appI18n.t("cmcMigration:volumeTooltip.mountPointValue", {
                                        value: device.blockDevice.mountPoint,
                                    })}
                                </Box>
                            )}
                            <Box sx={{ wordBreak: "break-all" }}>
                                {appI18n.t("cmcMigration:volumeTooltip.pathValue", {
                                    value: volume.getDevicePath(),
                                })}
                            </Box>
                        </Box>
                    }
                    arrow={true}
                >
                    <SvgIcon color={"primary"}>
                        <AiOutlineInfoCircle />
                    </SvgIcon>
                </Tooltip>
            </Box>
        );
    }
    return null;
};

export const getMigrationSourceVolumeLabel = (
    sourceVolume: GalaxyMigrateManagedVolumeInfo,
    storageConfig: GalaxyMigrateStorageConfig,
    isDeploymentWindows: boolean
) => {
    let tooltip;
    let label = (
        <Typography variant={"h6"} align={"center"}>
            {sourceVolume.getName()}
        </Typography>
    );
    if (!!storageConfig) {
        const device = getStorageConfigBlockDevice(sourceVolume.getDevicePath(), storageConfig.toObject().devicesList, isDeploymentWindows);
        if (!!device) {
            if (isDeploymentWindows && !!device?.blockDevice.fsType) {
                label = (
                    <Typography variant={"h6"} align={"center"}>
                        {`${device.blockDevice.fsType} (${device.blockDevice.mountPoint})`}
                    </Typography>
                );
            } else {
                label = (
                    <Typography variant={"h6"} align={"center"}>
                        {device.blockDevice.deviceName ?? sourceVolume.getName()}
                    </Typography>
                );
            }
            tooltip = getMigrationVolumeTooltip(device, sourceVolume);
        }
    }
    return (
        <Box display={"flex"} justifyContent={"center"} alignItems={"center"}>
            {label} &nbsp; {tooltip}
        </Box>
    );
};

export const getMigrationDestinationVolumeLabel = (
    destinationVolume: GalaxyMigrateManagedVolumeInfo,
    storageConfig: GalaxyMigrateStorageConfig,
    isDeploymentWindows: boolean,
    destinationDeployment: DeploymentIdentityInfo,
    remoteStorageConfig: GalaxyMigrateStorageConfig
) => {
    let tooltip;
    let label = (
        <Typography variant={"h6"} align={"center"}>
            {destinationVolume.getName()}
        </Typography>
    );
    let device;
    if (!!destinationDeployment && !!remoteStorageConfig) {
        device = getStorageConfigBlockDevice(destinationVolume.getDevicePath(), remoteStorageConfig.toObject().devicesList, isDeploymentWindows);
    } else if (!!storageConfig) {
        device = getStorageConfigBlockDevice(destinationVolume.getDevicePath(), storageConfig.toObject().devicesList, isDeploymentWindows);
    }

    if (!!device) {
        if (isDeploymentWindows && !!device?.blockDevice?.fsType) {
            label = (
                <Typography variant={"h6"} align={"center"}>
                    {`${device.blockDevice.fsType} (${device.blockDevice.mountPoint})`}
                </Typography>
            );
        } else {
            label = (
                <Typography variant={"h6"} align={"center"}>
                    {device.blockDevice.deviceName ?? destinationVolume.getName()}
                </Typography>
            );
        }
        tooltip = getMigrationVolumeTooltip(device, destinationVolume);
    }

    return (
        <Box display={"flex"} justifyContent={"center"} alignItems={"center"}>
            {label} &nbsp; {tooltip}
        </Box>
    );
};

export interface ActionTriggeredState {
    actionTriggered: boolean;
    setActionTriggered: (triggered: boolean) => void;
}

const getIntervalEndMinute = (start: Date, end: Date) => {
    const midnightIntervalEnd = 23 * 60 + 59;
    if (end.getHours() === 0 && end.getMinutes() === 0) {
        return midnightIntervalEnd;
    }
    return getMinutesFromTime(end) - 1;
};

export const createMigrationQosSchedule = (formValues: MigrationQosScheduleType): MigrationSession.ConfigParams.QosSchedule => {
    const qosSchedule = new MigrationSession.ConfigParams.QosSchedule();
    const dailyScheduleIntervalsList: Array<MigrationSession.ConfigParams.QosSchedule.DailySchedule.Interval> = [];
    const fridaysIntervalsList: Array<MigrationSession.ConfigParams.QosSchedule.DailySchedule.Interval> = [];
    const weekendsIntervalsList: Array<MigrationSession.ConfigParams.QosSchedule.DailySchedule.Interval> = [];

    if (formValues.daily.length > 0) {
        for (const timeSlot of formValues.daily) {
            const interval = new MigrationSession.ConfigParams.QosSchedule.DailySchedule.Interval()
                .setStart(getMinutesFromTime(timeSlot.start))
                .setEnd(getIntervalEndMinute(timeSlot.start, timeSlot.end))
                .setQosLevel(timeSlot.qosLevel);
            dailyScheduleIntervalsList.push(interval);
        }
        const dailySchedule = new MigrationSession.ConfigParams.QosSchedule.DailySchedule().setIntervalsList(dailyScheduleIntervalsList);
        qosSchedule.setMonday(dailySchedule);
        qosSchedule.setTuesday(dailySchedule);
        qosSchedule.setWednesday(dailySchedule);
        qosSchedule.setThursday(dailySchedule);
        qosSchedule.setFriday(dailySchedule);
        qosSchedule.setSaturday(dailySchedule);
        qosSchedule.setSunday(dailySchedule);
    }

    if (formValues.fridays.length > 0) {
        for (const timeSlot of formValues.fridays) {
            const interval = new MigrationSession.ConfigParams.QosSchedule.DailySchedule.Interval()
                .setStart(getMinutesFromTime(timeSlot.start))
                .setEnd(getIntervalEndMinute(timeSlot.start, timeSlot.end))
                .setQosLevel(timeSlot.qosLevel);
            fridaysIntervalsList.push(interval);
        }
        const fridaySchedule = new MigrationSession.ConfigParams.QosSchedule.DailySchedule().setIntervalsList(fridaysIntervalsList);
        qosSchedule.setFriday(fridaySchedule);
    }

    if (formValues.weekends.length > 0) {
        for (const timeSlot of formValues.weekends) {
            const interval = new MigrationSession.ConfigParams.QosSchedule.DailySchedule.Interval()
                .setStart(getMinutesFromTime(timeSlot.start))
                .setEnd(getIntervalEndMinute(timeSlot.start, timeSlot.end))
                .setQosLevel(timeSlot.qosLevel);
            weekendsIntervalsList.push(interval);
        }
        const weekendSchedule = new MigrationSession.ConfigParams.QosSchedule.DailySchedule().setIntervalsList(weekendsIntervalsList);
        qosSchedule.setSaturday(weekendSchedule);
        qosSchedule.setSunday(weekendSchedule);
    }

    return qosSchedule;
};

const checkIntervalListIsEqual = (
    a: Array<MigrationSession.ConfigParams.QosSchedule.DailySchedule.Interval>,
    b: Array<MigrationSession.ConfigParams.QosSchedule.DailySchedule.Interval>
) => {
    if (!a || !b) {
        return false;
    }
    if (a.length !== b.length) {
        return false;
    }
    for (let i = 0; i < a.length; i++) {
        if (a[i].getStart() !== b[i].getStart() || a[i].getEnd() !== b[i].getEnd() || a[i].getQosLevel() !== b[i].getQosLevel()) {
            return false;
        }
    }
    return true;
};

export const convertQosScheduleToFormValues = (qosSchedule: MigrationSession.ConfigParams.QosSchedule): MigrationQosScheduleType => {
    const formValues: MigrationQosScheduleType = {
        daily: [],
        fridays: [],
        weekends: [],
    };
    if (qosSchedule.hasMonday()) {
        for (const interval of qosSchedule.getMonday().getIntervalsList()) {
            const timeSlot: QosDailyScheduleIntervalType = {
                start: createDateObjectFromMinutes(interval.getStart()),
                end: createDateObjectFromMinutes(interval.getEnd() + 1),
                qosLevel: interval.getQosLevel(),
            };
            formValues.daily.push(timeSlot);
        }
    }
    if (qosSchedule.hasFriday() && !checkIntervalListIsEqual(qosSchedule.getMonday()?.getIntervalsList(), qosSchedule.getFriday().getIntervalsList())) {
        for (const interval of qosSchedule.getFriday().getIntervalsList()) {
            const timeSlot: QosDailyScheduleIntervalType = {
                start: createDateObjectFromMinutes(interval.getStart()),
                end: createDateObjectFromMinutes(interval.getEnd() + 1),
                qosLevel: interval.getQosLevel(),
            };
            formValues.fridays.push(timeSlot);
        }
    }
    if (qosSchedule.hasSaturday() && !checkIntervalListIsEqual(qosSchedule.getMonday()?.getIntervalsList(), qosSchedule.getSaturday().getIntervalsList())) {
        for (const interval of qosSchedule.getSaturday().getIntervalsList()) {
            const timeSlot: QosDailyScheduleIntervalType = {
                start: createDateObjectFromMinutes(interval.getStart()),
                end: createDateObjectFromMinutes(interval.getEnd() + 1),
                qosLevel: interval.getQosLevel(),
            };
            formValues.weekends.push(timeSlot);
        }
    }
    return formValues;
};

export const getImpactLevelOptions = (): FormSelectableCardConfig[] => [
    {
        title: getSyncQosImpactLevelLabel(SyncQos.ImpactLevel.MINIMUM),
        description: appI18n.t("cmcMigration:qos.options.minimumDescription"),
        icon: <img src={MinimumMode} height={72} alt={appI18n.t("cmcMigration:qos.options.minimumImageAlt")} />,
        value: SyncQos.ImpactLevel.MINIMUM,
    },
    {
        title: getSyncQosImpactLevelLabel(SyncQos.ImpactLevel.MODERATE),
        description: appI18n.t("cmcMigration:qos.options.moderateDescription"),
        icon: <img src={ModerateMode} height={72} alt={appI18n.t("cmcMigration:qos.options.moderateImageAlt")} />,
        value: SyncQos.ImpactLevel.MODERATE,
    },
    {
        title: getSyncQosImpactLevelLabel(SyncQos.ImpactLevel.AGGRESSIVE),
        description: appI18n.t("cmcMigration:qos.options.aggressiveDescription"),
        icon: <img src={AggressiveMode} height={72} alt={appI18n.t("cmcMigration:qos.options.aggressiveImageAlt")} />,
        value: SyncQos.ImpactLevel.AGGRESSIVE,
    },
    {
        title: getSyncQosImpactLevelLabel(SyncQos.ImpactLevel.RELENTLESS),
        description: appI18n.t("cmcMigration:qos.options.relentlessDescription"),
        icon: <img src={RelentlessMode} height={72} alt={appI18n.t("cmcMigration:qos.options.relentlessImageAlt")} />,
        value: SyncQos.ImpactLevel.RELENTLESS,
    },
];

export const getImpactLevelConfig = (impactLevel: SyncQos.ImpactLevel): FormSelectableCardConfig => {
    return getImpactLevelOptions().find((o) => o.value === impactLevel);
};

export const useLinearProgressStyle = (darkBackground?: boolean) => {
    const theme = useTheme();
    return {
        borderRadius: 5,
        backgroundColor: darkBackground ? theme.palette.cirrus.main : "",

        [`& .${linearProgressClasses.bar}`]: {
            borderRadius: 5,
        },
    };
};
